'use client'

import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { ReactNode } from 'react'
import { createClient } from '@/lib/supabase/client'
import { logger } from '@/lib/logger'
import type { User as SupabaseUser } from '@supabase/supabase-js'

// ─────────────────────────────────────────────────────────────────────────────
// Auth State — managed by supabase-js internally.
//
// supabase-js handles:
//   - State machine (loading → authenticated | unauthenticated)
//   - Token refresh (auto, before expiration)
//   - Session persistence (httpOnly cookie via @supabase/ssr)
//   - Race conditions (no manual AbortController needed)
//
// This context is a thin wrapper that exposes the user + login/logout.
// The public interface ({ state, user, loading, isAuthenticated, login, logout })
// is preserved so consumers (useAuth, useProtectedRoute, login pages) don't change.
// ─────────────────────────────────────────────────────────────────────────────

export type AuthState = 'loading' | 'authenticated' | 'unauthenticated'

export interface User {
  id: string
  email: string
  role: 'OWNER' | 'ADMIN' | 'BARBER'
  first_name: string
  last_name: string
  tenantId: string
}

interface AuthContextValue {
  state: AuthState
  user: User | null
  loading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<{ success: boolean; user?: User; error?: string }>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * Map a Supabase user (GoTrue JWT claims) to the existing User shape.
 * GoTrue stores app_metadata in the JWT (read-only from client).
 */
function mapUser(supabaseUser: SupabaseUser): User {
  return {
    id: supabaseUser.id,
    email: supabaseUser.email || '',
    role: (supabaseUser.app_metadata?.role as User['role']) || 'BARBER',
    first_name: supabaseUser.app_metadata?.first_name || '',
    last_name: supabaseUser.app_metadata?.last_name || '',
    tenantId: supabaseUser.app_metadata?.tenant_id || '',
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const supabase = createClient()
  const [user, setUser] = useState<User | null>(null)
  const [state, setState] = useState<AuthState>('loading')

  useEffect(() => {
    // getSession is synchronous from cookie (SSR) or localStorage.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser(mapUser(session.user))
        setState('authenticated')
      } else {
        setUser(null)
        setState('unauthenticated')
      }
    }).catch((err) => {
      logger.error('Auth init error:', err)
      setUser(null)
      setState('unauthenticated')
    })

    // onAuthStateChange replaces AbortController + auth:logout event.
    // supabase-js fires this on login, logout, token refresh, and session expiry.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser(mapUser(session.user))
        setState('authenticated')
      } else {
        setUser(null)
        setState('unauthenticated')
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) {
        return { success: false, error: error.message }
      }
      if (data.user) {
        const mappedUser = mapUser(data.user)
        setUser(mappedUser)
        setState('authenticated')
        return { success: true, user: mappedUser }
      }
      return { success: false, error: 'Login failed' }
    } catch (error) {
      logger.error('Login error:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Login failed',
      }
    }
  }, [supabase])

  const logout = useCallback(async () => {
    try {
      await supabase.auth.signOut()
    } catch (error) {
      logger.error('Logout error:', error)
    } finally {
      setUser(null)
      setState('unauthenticated')
    }
  }, [supabase])

  const loading = state === 'loading'

  return (
    <AuthContext.Provider
      value={{ state, user, loading, isAuthenticated: state === 'authenticated', login, logout }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider')
  }
  return context
}
